import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, Phone, MapPin, Clock, ChevronDown, ChevronRight,
  Car, Wrench, Banknote, Home, Calendar
} from 'lucide-react';
import { settingsService } from '../services/settingsService';
import MegaMenu from './MegaMenu';
import BookServiceModal from './BookServiceModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookServiceOpen, setIsBookServiceOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [settings, setSettings] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const location = useLocation();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const { data } = await settingsService.getSettings();
    if (data) {
      setSettings(data);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine if scrolled
      setIsScrolled(currentScrollY > 50);
      
      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past 100px
        setIsVisible(false);
      } else {
        // Scrolling up
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setOpenAccordion(null);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  // Mobile drawer information architecture
  const mobilePrimary = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Automobiles', href: '/inventory', icon: Car },
  ];

  const mobileSections = [
    {
      id: 'services', label: 'Repair Services', icon: Wrench,
      links: [
        { name: 'All Services', href: '/services' },
        { name: 'European Vehicles', href: '/services/european-car-repair' },
        { name: 'Diesel Repair', href: '/services/dallas-diesel-paradise-pros' },
        { name: 'BMW Service', href: '/services/european-car-repair/bmw' },
        { name: 'Mercedes Service', href: '/services/european-car-repair/mercedes' },
        { name: 'Audi Service', href: '/services/european-car-repair/audi' },
        { name: 'Porsche Service', href: '/services/european-car-repair/porsche' },
        { name: 'Volkswagen Service', href: '/services/european-car-repair/volkswagen' },
        { name: 'Check Engine Light', href: '/services/diagnostic-check-engine-light' },
        { name: 'Clutch Repair', href: '/services/clutch-repair' },
        { name: 'Wheel & Tire', href: '/services/wheel-and-tire' },
      ],
    },
    {
      id: 'dealership', label: 'Buy & Finance', icon: Banknote,
      links: [
        { name: 'Get Pre-Qualified', href: '/pre-approval' },
        { name: 'Finance Application', href: '/finance-application' },
        { name: 'Trade-In Value', href: '/trade-in' },
        { name: 'Parts & Accessories', href: '/parts' },
        { name: 'My Payments', href: '/my-payments' },
      ],
    },
  ];

  const mobileSecondary = [
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <>
      {/* Top Bar - Simplified */}
      <div className="bg-red-600 text-white py-2 hidden lg:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <a href="tel:8173822316" className="flex items-center space-x-2 transition-colors hover:text-white/80">
                <Phone className="w-4 h-4" />
                <span>{settings?.phone || '(817) 382-2316'}</span>
              </a>
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{settings?.address ? settings.address.split(',')[0] : '305 Co Rd 3470'}, Paradise, TX</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{settings?.businessHours ? `Mon-Fri: ${settings.businessHours.monday?.open || '8AM'}-${settings.businessHours.friday?.close || '5PM'}` : 'Mon-Fri: 8AM-5PM'}</span>
              </div>
              <Link to="/inventory" className="text-gray-300 hover:text-white transition-colors">
                Automobiles
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`fixed top-0 left-0 right-0 w-full border-b transition-all duration-300 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-xl border-gray-200/70 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)]'
            : 'bg-white/95 backdrop-blur-md border-gray-100 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_6px_24px_-18px_rgba(0,0,0,0.12)]'
        } ${isVisible ? 'translate-y-0' : 'lg:-translate-y-full'}`}
        style={{ zIndex: 9996 }}
      >
        {/* Desktop bar: full-width with better responsive layout */}
        <div className="hidden lg:flex items-center justify-between h-20 container mx-auto px-6 xl:px-6 2xl:px-8">
          {/* Left side: Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/assets/assets/images/logo.webp"
              alt="Paradise-Performance Logo"
              className="h-12 xl:h-14"
            />
          </Link>

          {/* Mega Menu Navigation */}
          <MegaMenu onBookService={() => setIsBookServiceOpen(true)} />
        </div>

        {/* Mobile bar: logo left, hamburger right */}
        <div className="flex lg:hidden justify-between items-center h-16 w-full px-4">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/assets/images/logo.webp"
              alt="Paradise-Performance Logo"
              className="h-10"
            />
          </Link>
          <button
            className={`p-2.5 rounded-lg transition-colors ${isMenuOpen ? 'bg-gray-100' : 'hover:bg-gray-100'} relative z-[60]`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {isMenuOpen ? <X className="w-6 h-6 text-gray-800" /> : <Menu className="w-6 h-6 text-gray-800" />}
          </button>
        </div>

        {/* ---- Mobile menu ---- */}
        <AnimatePresence>
          {isMenuOpen && (
            <>
              {/* Scrim */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm lg:hidden"
                style={{ zIndex: 9998 }}
                onClick={() => setIsMenuOpen(false)}
              />

              {/* Drawer */}
              <motion.aside
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', stiffness: 380, damping: 38 }}
                className="fixed top-0 right-0 flex h-[100dvh] w-[86%] max-w-sm flex-col bg-white lg:hidden"
                style={{ zIndex: 9999 }}
                aria-label="Mobile menu"
              >
                {/* Header strip — light, full-color logo, red accent rule */}
                <div className="relative bg-white px-5 pb-4 pt-4">
                  <span className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-red-600 to-red-500" />
                  <div className="flex items-center justify-between">
                    <img
                      src="/assets/assets/images/logo.webp"
                      alt="Paradise-Performance"
                      className="h-11"
                    />
                    <button
                      onClick={() => setIsMenuOpen(false)}
                      className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                      aria-label="Close menu"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <a
                    href="tel:8173822316"
                    className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-red-600"
                  >
                    <Phone className="h-4 w-4 text-red-600" />
                    {settings?.phone || '(817) 382-2316'}
                  </a>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-4">
                  {/* Primary links */}
                  <div className="space-y-1">
                    {mobilePrimary.map((link) => {
                      const active = isActive(link.href);
                      return (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setIsMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-semibold transition-colors ${
                            active ? 'bg-red-50 text-red-600' : 'text-slate-800 hover:bg-slate-50'
                          }`}
                        >
                          <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${active ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                            <link.icon className="h-[18px] w-[18px]" />
                          </span>
                          {link.name}
                        </Link>
                      );
                    })}
                  </div>

                  {/* Accordion sections */}
                  <div className="mt-3 space-y-1">
                    {mobileSections.map((section) => {
                      const open = openAccordion === section.id;
                      return (
                        <div key={section.id} className="rounded-xl">
                          <button
                            onClick={() => setOpenAccordion(open ? null : section.id)}
                            aria-expanded={open}
                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-semibold transition-colors ${
                              open ? 'bg-slate-50 text-slate-900' : 'text-slate-800 hover:bg-slate-50'
                            }`}
                          >
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                              <section.icon className="h-[18px] w-[18px]" />
                            </span>
                            <span className="flex-1 text-left">{section.label}</span>
                            <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                          </button>

                          <AnimatePresence initial={false}>
                            {open && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                className="overflow-hidden"
                              >
                                <div className="ml-6 my-1 space-y-0.5 border-l border-slate-100 pl-3">
                                  {section.links.map((item) => {
                                    const active = isActive(item.href);
                                    return (
                                      <Link
                                        key={item.href}
                                        to={item.href}
                                        onClick={() => setIsMenuOpen(false)}
                                        className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                          active ? 'font-semibold text-red-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                        }`}
                                      >
                                        {item.name}
                                        <ChevronRight className={`h-4 w-4 ${active ? 'text-red-500' : 'text-slate-300'}`} />
                                      </Link>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  {/* Secondary links */}
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    {mobileSecondary.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors ${
                          isActive(link.href) ? 'text-red-600' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Sticky bottom CTAs */}
                <div className="border-t border-slate-200 bg-white px-4 py-4 space-y-2.5">
                  <Link
                    to="/inventory"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3.5 font-semibold text-white shadow-sm shadow-red-600/25 transition-colors hover:bg-red-700"
                  >
                    <Car className="h-5 w-5" /> Browse Vehicles
                  </Link>
                  <button
                    onClick={() => { setIsMenuOpen(false); setIsBookServiceOpen(true); }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 px-5 py-3.5 font-semibold text-slate-800 transition-colors hover:border-red-600 hover:text-red-600"
                  >
                    <Calendar className="h-5 w-5" /> Schedule Service
                  </button>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </header>
      <BookServiceModal isOpen={isBookServiceOpen} onClose={() => setIsBookServiceOpen(false)} />
    </>
  );
};

export default Header;